import { useCallback, useEffect, useState, Fragment } from "react";
import { Boxes, CheckCircle2, Download, Globe2, Loader2, MapPin, PackageCheck, Printer, RefreshCw, ScanLine, Scale, Send, Settings2, TriangleAlert } from "lucide-react";
import api, { inr, fmtDate, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

function Margins() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/international/margins").then(({ data }) => setM(data)).catch(() => {}); }, []);
  if (!m) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-4" data-testid="intl-margins">
      {[["International Revenue", m.international_revenue, "text-shipany-navy"], ["IndiaFast Amount", m.indiafast_amount, "text-slate-500"], ["Domestic Forwarding Cost", m.domestic_forwarding_cost, "text-amber-600"], ["Net Shipping Margin", m.net_shipping_margin, "text-emerald-600"]].map(([l, v, c]) => (
        <div key={l} className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
          <p className="text-[10px] uppercase tracking-widest text-slate-400">{l}</p>
          <p className={`mono mt-1 text-xl font-bold ${c}`}>{inr(v)}</p>
        </div>
      ))}
    </div>
  );
}

function ForwardingCenter() {
  const [rows, setRows] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [scanned, setScanned] = useState(null);
  const [rcv, setRcv] = useState({ actual_weight: "", final_amount: "", length: "", width: "", height: "", track_status: "INTL_IN_TRANSIT", track_note: "", track_location: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/forwarding/incoming").then(({ data }) => setRows(data)).catch((e) => toast.error(fmtErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/forwarding/scan", { code: scanCode });
      setScanned(data);
      setRcv({ actual_weight: String(data.total_billed_weight || ""), final_amount: String(data.customer_amount ?? ""), length: "", width: "", height: "", track_status: "INTL_IN_TRANSIT", track_note: "", track_location: "" });
    } catch (e) { toast.error(fmtErr(e)); setScanned(null); } finally { setBusy(false); }
  };

  const receive = async (confirmDiff = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/forwarding/${scanned.forwarding.shipment_id}/receive`, {
        actual_weight: parseFloat(rcv.actual_weight) || undefined,
        final_amount: rcv.final_amount !== "" ? parseFloat(rcv.final_amount) : undefined,
        length: parseFloat(rcv.length) || undefined, width: parseFloat(rcv.width) || undefined, height: parseFloat(rcv.height) || undefined,
        tracking_status: rcv.track_note.trim() ? rcv.track_status : undefined,
        tracking_note: rcv.track_note.trim() || undefined,
        tracking_location: rcv.track_location.trim() || undefined,
        confirm_diff: confirmDiff,
      });
      if (data.requires_confirmation) {
        toast.warning(`Weight Difference Detected: expected ${data.expected_weight} kg, received ${data.actual_weight} kg (${data.weight_difference > 0 ? "+" : ""}${data.weight_difference} kg). Confirm to accept.`, {
          action: { label: "Confirm Anyway", onClick: () => receive(true) }, duration: 10000,
        });
      } else {
        toast.success(data.settlement && !data.settlement.skipped
          ? `Received — final bill ${inr(data.settlement.final_amount)} settled from the customer's wallet`
          : "Marked received at IndiaFast forwarding center");
        setScanned(null); setScanCode(""); load();
      }
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const dispatchIntl = async (fwdId) => {
    try {
      await api.post(`/admin/forwarding/${fwdId}/dispatch`);
      toast.success("International shipment marked dispatched");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const [trackFor, setTrackFor] = useState(null);
  const [trackForm, setTrackForm] = useState({ status: "INTL_IN_TRANSIT", note: "", location: "" });

  const addTrack = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/international/${trackFor}/tracking-event`, trackForm);
      toast.success("International tracking update added — visible to the customer");
      setTrackFor(null);
      setTrackForm({ status: "INTL_IN_TRANSIT", note: "", location: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const retry = async (sid) => {
    try {
      await api.post(`/admin/international/${sid}/retry-forwarding`);
      toast.success("Domestic forwarding booked");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const regenerate = async (sid) => {
    try {
      await api.post(`/admin/international/${sid}/regenerate-label`);
      toast.success("Label cache cleared — next download refetches the label");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="scan-panel">
        <p className="font-display text-sm font-bold text-shipany-navy">Receive a package</p>
        <div className="mt-3 flex gap-3">
          <div className="relative flex-1">
            <ScanLine className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="scan-input" className={`${inputCls} mono pl-9`} placeholder="Scan / enter domestic AWB, FWD order no, IF-INT number or international AWB"
              value={scanCode} onChange={(e) => setScanCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scan()} />
          </div>
          <button onClick={scan} disabled={busy || !scanCode.trim()} data-testid="scan-btn"
            className="rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open Shipment"}
          </button>
        </div>

        {scanned && (
          <div className="mt-4 rounded-xl bg-slate-50 p-5" data-testid="scan-result">
            <div className="grid gap-3 sm:grid-cols-4">
              <div><p className="text-[10px] uppercase tracking-widest text-slate-400">Customer</p><p className="text-sm font-semibold text-shipany-navy">{scanned.customer_name}</p><p className="text-xs text-slate-400">{scanned.customer_email}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-400">International Shipment</p><p className="mono text-sm font-bold text-shipany-blue">{scanned.shipment_number}</p><p className="mono text-xs text-slate-400">{scanned.international_awb}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-400">Destination</p><p className="text-sm font-semibold text-shipany-navy">{scanned.destination_country_name}</p><p className="text-xs text-slate-400">{scanned.total_boxes} boxes · expected {scanned.total_billed_weight} kg</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-400">Payment</p>
                <p className="text-sm font-semibold text-shipany-navy" data-testid="scan-payment-mode">{scanned.payment_mode === "cash" ? "Cash — pay after final bill" : "Wallet — prepaid"}</p>
                <p className="text-xs text-slate-400">{scanned.payment_status === "settled" ? `Settled ${inr(scanned.final_amount)}` : scanned.payment_mode === "cash" ? `Estimate ${inr(scanned.customer_amount)} · final bill pending` : `Charged ${inr(scanned.customer_amount)}`}</p></div>
            </div>
            {scanned.forwarding ? (
              <>
              <div className="mt-4 grid gap-3 sm:grid-cols-6">
                {[["actual_weight", "Actual received weight (kg)"], ["final_amount", "Final bill amount (₹)"], ["length", "L (cm)"], ["width", "W (cm)"], ["height", "H (cm)"]].map(([k, l]) => (
                  <div key={k}><label className="text-[10px] font-semibold uppercase text-slate-400">{l}</label>
                    <input data-testid={`receive-${k}`} className={`${inputCls} mono mt-0.5`} value={rcv[k]} onChange={(e) => setRcv({ ...rcv, [k]: e.target.value })} /></div>
                ))}
                <div className="flex items-end gap-2">
                  <button onClick={() => receive(false)} disabled={busy} data-testid="confirm-received-btn"
                    className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                    <PackageCheck className="h-4 w-4" /> Confirm Received
                  </button>
                </div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-4">
                <div><label className="text-[10px] font-semibold uppercase text-slate-400">Intl tracking status (optional)</label>
                  <select data-testid="receive-track-status" className={`${inputCls} mt-0.5`} value={rcv.track_status} onChange={(e) => setRcv({ ...rcv, track_status: e.target.value })}>
                    <option value="INTL_IN_TRANSIT">International In Transit</option>
                    <option value="CUSTOMS_CLEARANCE">Customs Clearance</option>
                    <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="NDR">Delivery Failed (NDR)</option>
                    <option value="RTO">Return to Origin (RTO)</option>
                    <option value="INTL_EXCEPTION">Exception / Held</option>
                  </select></div>
                <div><label className="text-[10px] font-semibold uppercase text-slate-400">Location (optional)</label>
                  <input data-testid="receive-track-location" className={`${inputCls} mt-0.5`} placeholder="e.g. Delhi Airport" value={rcv.track_location} onChange={(e) => setRcv({ ...rcv, track_location: e.target.value })} /></div>
                <div className="sm:col-span-2"><label className="text-[10px] font-semibold uppercase text-slate-400">Tracking note (optional — shown to customer)</label>
                  <input data-testid="receive-track-note" className={`${inputCls} mt-0.5`} placeholder="e.g. Departed from origin hub" value={rcv.track_note} onChange={(e) => setRcv({ ...rcv, track_note: e.target.value })} /></div>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">On receive, the final bill amount above is settled from the customer's wallet (cash bookings are charged in full; prepaid bookings are adjusted by the difference — balance may go due). Tracking fields are optional.</p>
              </>
            ) : (
              <p className="mt-3 text-xs font-medium text-amber-600">No domestic forwarding shipment linked yet — retry forwarding first.</p>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="incoming-table">
        <table className="w-full min-w-[1100px] text-sm">
          <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
            <th className="px-4 py-3">Shipment ID</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Domestic AWB</th>
            <th className="px-4 py-3">Intl AWB</th><th className="px-4 py-3">Courier</th><th className="px-4 py-3">Origin</th>
            <th className="px-4 py-3">Destination</th><th className="px-4 py-3">Boxes</th><th className="px-4 py-3">Weight</th>
            <th className="px-4 py-3">Expected</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <Fragment key={r.shipment_id}>
              <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`incoming-${r.domestic_awb}`}>
                <td className="mono px-4 py-3 text-xs font-semibold text-shipany-blue">{r.shipment_number}</td>
                <td className="px-4 py-3 text-xs">{r.customer_name}<p className="text-slate-400">{r.customer_email}</p></td>
                <td className="mono px-4 py-3 text-xs">{r.domestic_awb}</td>
                <td className="mono px-4 py-3 text-xs">{r.international_awb}</td>
                <td className="px-4 py-3 text-xs">{r.courier_name}</td>
                <td className="px-4 py-3 text-xs">{r.origin_city}</td>
                <td className="px-4 py-3 text-xs">{r.destination_country_name}</td>
                <td className="px-4 py-3 text-xs">{r.total_boxes}</td>
                <td className="mono px-4 py-3 text-xs">{r.weight} kg{r.received_weight ? ` → ${r.received_weight} kg` : ""}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(r.eta_date)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.intl_status === "FORWARDING_FAILED" ? "bg-red-100 text-red-600" : "bg-shipany-blue/10 text-shipany-blue"}`}>{r.intl_stage_label || r.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {r.intl_status === "FORWARDING_FAILED" && (
                      <button onClick={() => retry(r.international_shipment_id)} title="Retry Domestic Booking" data-testid={`retry-${r.international_shipment_id}`}
                        className="rounded-lg p-2 text-amber-500 hover:bg-amber-50"><RefreshCw className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => regenerate(r.international_shipment_id)} title="Regenerate Label" data-testid={`regen-${r.international_shipment_id}`}
                      className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><Printer className="h-4 w-4" /></button>
                    <a href={`${API_BASE}/api/intl/shipments/${r.international_shipment_id}/label?type=international`} target="_blank" rel="noreferrer" title="Print International Label" data-testid={`print-intl-${r.international_shipment_id}`}
                      className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><Download className="h-4 w-4" /></a>
                    {r.status === "DELIVERED" && r.intl_status !== "INTL_DISPATCHED" && (
                      <button onClick={() => dispatchIntl(r.shipment_id)} title="Dispatch Internationally" data-testid={`dispatch-${r.shipment_id}`}
                        className="rounded-lg p-2 text-emerald-500 hover:bg-emerald-50"><Send className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => { setTrackFor(trackFor === r.international_shipment_id ? null : r.international_shipment_id); setTrackForm({ status: "INTL_IN_TRANSIT", note: "", location: "" }); }}
                      title="Add International Tracking Update" data-testid={`add-track-${r.international_shipment_id}`}
                      className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue"><MapPin className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
              {trackFor === r.international_shipment_id && (
                <tr className="border-t border-slate-100 bg-slate-50/60" data-testid={`track-form-${r.international_shipment_id}`}>
                  <td colSpan={12} className="px-4 py-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Add international leg tracking — {r.shipment_number}</p>
                    <div className="mt-2 grid gap-3 sm:grid-cols-4">
                      <div><label className="text-[10px] font-semibold uppercase text-slate-400">Status</label>
                        <select data-testid="track-status-select" className={`${inputCls} mt-0.5`} value={trackForm.status} onChange={(e) => setTrackForm({ ...trackForm, status: e.target.value })}>
                          <option value="INTL_IN_TRANSIT">International In Transit</option>
                          <option value="CUSTOMS_CLEARANCE">Customs Clearance</option>
                          <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                          <option value="DELIVERED">Delivered</option>
                          <option value="NDR">Delivery Failed (NDR)</option>
                          <option value="RTO">Return to Origin (RTO)</option>
                          <option value="INTL_EXCEPTION">Exception / Held</option>
                        </select></div>
                      <div><label className="text-[10px] font-semibold uppercase text-slate-400">Location</label>
                        <input data-testid="track-location-input" className={`${inputCls} mt-0.5`} placeholder="e.g. Delhi Airport / Cummings, USA" value={trackForm.location} onChange={(e) => setTrackForm({ ...trackForm, location: e.target.value })} /></div>
                      <div className="sm:col-span-2"><label className="text-[10px] font-semibold uppercase text-slate-400">Scan note (shown to customer)</label>
                        <input data-testid="track-note-input" className={`${inputCls} mt-0.5`} placeholder="e.g. Departed from origin hub" value={trackForm.note} onChange={(e) => setTrackForm({ ...trackForm, note: e.target.value })} /></div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button onClick={addTrack} disabled={busy || !trackForm.note.trim()} data-testid="track-save-btn"
                        className="rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-50">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Tracking Update"}
                      </button>
                      <button onClick={() => setTrackFor(null)} data-testid="track-cancel-btn" className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-bold text-slate-600">Cancel</button>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            {rows && !rows.length && <tr><td colSpan={12} className="px-6 py-12 text-center text-slate-400">No incoming international forwarding shipments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IntlSettings() {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [couriers, setCouriers] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/international/settings").then(({ data }) => {
      setS({ ...data, forwarding_address: data.forwarding_address || { company: "", contact: "", address: "", city: "", state: "", pin: "", phone: "", email: "" } });
    }).catch((e) => toast.error(fmtErr(e)));
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/couriers").then(({ data }) => setCouriers((data || []).filter((c) => c.enabled && c.provider_code === "shipmozo"))).catch(() => {});
  }, [load]);

  if (!s) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const fa = s.forwarding_address;
  const fwd = s.forwarding || {};

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/international/settings", {
        api_url: s.api_url, api_secret: s.api_secret, environment: s.environment, enabled: s.enabled,
        intl_divisor: parseFloat(s.intl_divisor) || 5000,
        api_key: apiKey || undefined,
        forwarding_address: fa.company ? fa : undefined,
        forwarding: fwd,
      });
      toast.success("International settings saved");
      setApiKey(""); load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="intl-settings">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="font-display text-sm font-bold text-shipany-navy">IndiaFast International API</p>
        <div className="mt-4 space-y-3">
          <div><label className="text-xs font-semibold text-slate-500">API URL</label>
            <input data-testid="settings-api-url" className={`${inputCls} mono mt-1`} value={s.api_url || ""} onChange={(e) => setS({ ...s, api_url: e.target.value })} /></div>
          <div><label className="text-xs font-semibold text-slate-500">API Key {s.api_key_set && <span className="text-emerald-600">(set ···{s.api_key_last4})</span>}</label>
            <input data-testid="settings-api-key" type="password" className={`${inputCls} mono mt-1`} placeholder={s.api_key_set ? "Leave blank to keep current key" : "ifk_…"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-500">API Secret</label>
            <input data-testid="settings-api-secret" type="password" className={`${inputCls} mono mt-1`} value={s.api_secret || ""} onChange={(e) => setS({ ...s, api_secret: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Environment</label>
              <select data-testid="settings-environment" className={`${inputCls} mt-1`} value={s.environment} onChange={(e) => setS({ ...s, environment: e.target.value })}>
                <option value="live">Live</option><option value="test">Test</option>
              </select></div>
            <div><label className="text-xs font-semibold text-slate-500">Volumetric divisor</label>
              <input data-testid="settings-divisor" className={`${inputCls} mono mt-1`} value={s.intl_divisor || 5000} onChange={(e) => setS({ ...s, intl_divisor: e.target.value })} /></div>
          </div>
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-600">
            <input type="checkbox" data-testid="settings-enabled" checked={!!s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
            International shipping enabled
          </label>
        </div>

        <p className="font-display mt-6 text-sm font-bold text-shipany-navy">Domestic Forwarding Configuration</p>
        <div className="mt-3 space-y-3">
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-600">
            <input type="checkbox" data-testid="settings-auto-forward" checked={!!fwd.auto_forward} onChange={(e) => setS({ ...s, forwarding: { ...fwd, auto_forward: e.target.checked } })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
            Automatically book domestic forwarding after international booking
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Forwarding courier selection</label>
              <select data-testid="settings-courier-mode" className={`${inputCls} mt-1`} value={fwd.courier_mode || "cheapest"} onChange={(e) => setS({ ...s, forwarding: { ...fwd, courier_mode: e.target.value } })}>
                <option value="cheapest">Shipmozo Delhivery slab (auto)</option>
                <option value="manual">Selected Shipmozo courier (manual)</option>
              </select></div>
            <div><label className="text-xs font-semibold text-slate-500">Forwarding weight</label>
              <select data-testid="settings-weight-mode" className={`${inputCls} mt-1`} value={fwd.weight_mode || "billed"} onChange={(e) => setS({ ...s, forwarding: { ...fwd, weight_mode: e.target.value } })}>
                <option value="billed">Billed weight (default)</option>
                <option value="actual">Actual weight</option>
                <option value="chargeable">Chargeable weight</option>
              </select></div>
          </div>
          {fwd.courier_mode === "manual" && (
            <div><label className="text-xs font-semibold text-slate-500">Forwarding courier *</label>
              <select data-testid="settings-courier-id" className={`${inputCls} mt-1`} value={fwd.courier_id || ""} onChange={(e) => setS({ ...s, forwarding: { ...fwd, courier_id: e.target.value } })}>
                <option value="">— Select courier —</option>
                {couriers.map((c) => <option key={c.courier_id} value={c.courier_id}>{c.name} · {c.provider_code}</option>)}
              </select></div>
          )}
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-600">
            <input type="checkbox" data-testid="settings-transfer-dims" checked={!!fwd.transfer_dimensions} onChange={(e) => setS({ ...s, forwarding: { ...fwd, transfer_dimensions: e.target.checked } })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
            Transfer box dimensions to domestic booking
          </label>
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-600">
            <input type="checkbox" data-testid="settings-expose-cost" checked={!!fwd.expose_internal_cost} onChange={(e) => setS({ ...s, forwarding: { ...fwd, expose_internal_cost: e.target.checked } })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
            Show internal forwarding cost on customer invoice (off = hidden)
          </label>
          <p className="rounded-xl bg-slate-50 px-4 py-2.5 text-[11px] text-slate-500">Auto forwarding uses live Shipmozo rates only: cheapest eligible Delhivery slab for one box, Delhivery 10kg MPS for 2+ boxes, then another Shipmozo courier if Delhivery is unavailable. Internal shipments are flagged INTERNAL FORWARDING with CUSTOMER_CHARGE = ₹0.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="font-display text-sm font-bold text-shipany-navy">Default IndiaFast International Forwarding Address</p>
        <p className="mt-1 text-xs text-slate-400">Every international shipment is first forwarded here via the domestic engine. Change anytime.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[["company", "Company *"], ["contact", "Contact person *"]].map(([k, l]) => (
            <div key={k}><label className="text-xs font-semibold text-slate-500">{l}</label>
              <input data-testid={`fa-${k}`} className={`${inputCls} mt-1`} value={fa[k] || ""} onChange={(e) => setS({ ...s, forwarding_address: { ...fa, [k]: e.target.value } })} /></div>
          ))}
          <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-500">Address *</label>
            <input data-testid="fa-address" className={`${inputCls} mt-1`} value={fa.address || ""} onChange={(e) => setS({ ...s, forwarding_address: { ...fa, address: e.target.value } })} /></div>
          {[["city", "City *"], ["state", "State"], ["pin", "PIN code * (6 digits)"], ["phone", "Phone *"], ["email", "Email"]].map(([k, l]) => (
            <div key={k}><label className="text-xs font-semibold text-slate-500">{l}</label>
              <input data-testid={`fa-${k}`} className={`${inputCls} mt-1 ${k === "pin" ? "mono" : ""}`} value={fa[k] || ""} onChange={(e) => setS({ ...s, forwarding_address: { ...fa, [k]: e.target.value } })} /></div>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save-btn"
          className="mt-5 flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-50 glow-blue">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save Settings
        </button>
      </div>
    </div>
  );
}

export default function AdminInternational() {
  const [tab, setTab] = useState("center");
  return (
    <div data-testid="admin-international-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-shipany-navy text-shipany-cyan"><Globe2 className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">IndiaFast International</h1>
            <p className="text-sm text-slate-500">Forwarding center operations + international shipping settings.</p>
          </div>
        </div>
        <div className="flex rounded-xl border border-slate-200 bg-white p-1" data-testid="intl-admin-tabs">
          {[["center", "Forwarding Center", ScanLine], ["settings", "Settings", Settings2]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-shipany-blue text-white" : "text-slate-500 hover:text-shipany-navy"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6 space-y-6">
        <Margins />
        {tab === "center" ? <ForwardingCenter /> : <IntlSettings />}
      </div>
      <div className="mt-6 grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-xs text-slate-500 sm:grid-cols-3" data-testid="intl-admin-notes">
        <p className="flex items-start gap-2"><Boxes className="mt-0.5 h-4 w-4 shrink-0 text-shipany-blue" /> Domestic forwarding legs are internal: customer charge ₹0, cost tracked for accounting.</p>
        <p className="flex items-start gap-2"><Scale className="mt-0.5 h-4 w-4 shrink-0 text-shipany-blue" /> Weight differences beyond ±0.5 kg / 10% require explicit confirmation at receiving.</p>
        <p className="flex items-start gap-2"><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /> Failed forwarding bookings never duplicate international bookings — use Retry from the table.</p>
      </div>
    </div>
  );
}
